import React, { useEffect, useState, useMemo } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Download, AlertTriangle, X, Check, Sparkles } from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import { format } from "date-fns";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const COURSE_TYPES = [
  { value: "", label: "—" },
  { value: "single", label: "Single (1-on-1)" },
  { value: "group", label: "Group" },
];
const WATCH_REASONS = [
  { value: "struggling", label: "Struggling academically" },
  { value: "behaviour", label: "Behaviour issues" },
  { value: "considering_withdrawal", label: "Considering withdrawal" },
  { value: "other", label: "Other" },
];

const EMPTY = { name: "", contact: "", email: "", cefr_level: "", cefr_override: false, course_type: "", program: "", tuition_rate_rmb: "", watch_flag: false, watch_reason: "", watch_note: "", notes: "" };

function CefrCell({ row, onChange }) {
  const lvl = row.cefr_level || "";
  return (
    <div className="flex items-center gap-1">
      <Select value={lvl || "_none"} onValueChange={(v)=>onChange({ cefr_level: v === "_none" ? "" : v, cefr_override: true })}>
        <SelectTrigger data-testid={`stu-${row.id}-cefr`} className="h-7 px-2 text-xs w-20"><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">—</SelectItem>
          {LEVELS.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      {row.cefr_source === "auto" && <Sparkles className="w-3 h-3 text-[hsl(var(--primary))]" aria-label="auto from placement test" />}
    </div>
  );
}

function WatchCell({ row, onChange }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <button
        type="button"
        onClick={()=>onChange({ watch_flag: !row.watch_flag })}
        data-testid={`stu-${row.id}-watch-toggle`}
        className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${row.watch_flag ? 'border-red-300 bg-red-50 text-red-700' : 'border-[hsl(var(--border))] bg-white text-[hsl(var(--muted-foreground))]'}`}
      >{row.watch_flag ? "Yes" : "No"}</button>
      {row.watch_flag && (
        <Select value={row.watch_reason || "other"} onValueChange={(v)=>onChange({ watch_reason: v })}>
          <SelectTrigger data-testid={`stu-${row.id}-watch-reason`} className="h-7 px-2 text-xs w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{WATCH_REASONS.map(r=><SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
}

export default function StudentsPanel() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const [watchFilter, setWatchFilter] = useState("_all");

  async function load() {
    try { const { data } = await api.get("/admin/students"); setRows(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function patchRow(id, patch) {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch, _dirty: true } : r));
    try {
      const payload = { ...patch };
      if (payload.tuition_rate_rmb === "" || payload.tuition_rate_rmb === null) delete payload.tuition_rate_rmb;
      const { data } = await api.patch(`/admin/students/${id}`, payload);
      setRows(prev => prev.map(r => r.id === id ? data : r));
    } catch (e) { toast.error(getErrorDetail(e)); load(); }
  }

  async function create(e) {
    e.preventDefault();
    if (!draft.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...draft };
      if (payload.tuition_rate_rmb === "" || payload.tuition_rate_rmb === null) delete payload.tuition_rate_rmb;
      else payload.tuition_rate_rmb = Number(payload.tuition_rate_rmb);
      if (!payload.email) delete payload.email;
      if (payload.cefr_level) payload.cefr_override = true;
      await api.post("/admin/students", payload);
      toast.success(`${draft.name} added`);
      setDraft(EMPTY); setCreating(false); load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  async function del(id, name) {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    try { await api.delete(`/admin/students/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  function exportCSV() {
    if (rows.length === 0) { toast.error("No students to export"); return; }
    downloadCSV(`dawnville-students-${format(new Date(), "yyyyMMdd-HHmm")}.csv`,
      rows.map(r => ({ ...r, watch_flag: r.watch_flag ? "yes" : "no" })),
      [
        { key: "name", label: "name" },
        { key: "contact", label: "contact" },
        { key: "email", label: "email" },
        { key: "cefr_level", label: "cefr_level" },
        { key: "course_type", label: "course_type" },
        { key: "program", label: "program" },
        { key: "tuition_rate_rmb", label: "tuition_rate_rmb" },
        { key: "watch_flag", label: "watch_flag" },
        { key: "watch_reason", label: "watch_reason" },
        { key: "watch_note", label: "watch_note" },
        { key: "notes", label: "notes" },
      ]
    );
    toast.success(`Exported ${rows.length} student(s)`);
  }

  async function importCSV(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error("Pick a .csv file"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/students/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} student(s)${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      if (data.errors?.length) console.warn("Import warnings:", data.errors);
      load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return rows.filter(r => {
      if (watchFilter === "watch" && !r.watch_flag) return false;
      if (watchFilter === "clear" && r.watch_flag) return false;
      if (!f) return true;
      return (r.name || "").toLowerCase().includes(f) ||
             (r.email || "").toLowerCase().includes(f) ||
             (r.contact || "").toLowerCase().includes(f) ||
             (r.program || "").toLowerCase().includes(f);
    });
  }, [rows, filter, watchFilter]);

  return (
    <div data-testid="admin-students-panel" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">
          Roster · {rows.length} {rows.filter(r=>r.watch_flag).length > 0 && <span className="text-red-600">· {rows.filter(r=>r.watch_flag).length} on watch-list</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={filter} onChange={(e)=>setFilter(e.target.value)} placeholder="Search name / email / program" data-testid="stu-filter" className="h-9 w-56" />
          <Select value={watchFilter} onValueChange={setWatchFilter}>
            <SelectTrigger data-testid="stu-watch-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All students</SelectItem>
              <SelectItem value="watch">On watch-list</SelectItem>
              <SelectItem value="clear">Not on watch-list</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="stu-export-csv" disabled={rows.length === 0} className="rounded-full"><Download className="w-3.5 h-3.5 mr-1.5" />Export CSV</Button>
          <label className="cursor-pointer rounded-full inline-flex items-center text-xs h-9 px-3 border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/60" data-testid="stu-import-csv-label">
            <Upload className="w-3.5 h-3.5 mr-1.5" />Import CSV
            <input type="file" accept=".csv" onChange={importCSV} data-testid="stu-import-csv" className="hidden" />
          </label>
          <Button size="sm" onClick={()=>setCreating(v=>!v)} data-testid="stu-new-toggle" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
            {creating ? <><X className="w-3.5 h-3.5 mr-1.5" />Cancel</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />New student</>}
          </Button>
        </div>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="stu-create-form">
          <div className="md:col-span-3"><Label className="text-xs">Name</Label><Input required value={draft.name} onChange={(e)=>setDraft({...draft, name: e.target.value})} data-testid="stu-new-name" className="mt-1" /></div>
          <div className="md:col-span-3"><Label className="text-xs">Contact (phone / WeChat)</Label><Input value={draft.contact} onChange={(e)=>setDraft({...draft, contact: e.target.value})} data-testid="stu-new-contact" className="mt-1" /></div>
          <div className="md:col-span-3"><Label className="text-xs">Email</Label><Input type="email" value={draft.email} onChange={(e)=>setDraft({...draft, email: e.target.value})} data-testid="stu-new-email" className="mt-1" /></div>
          <div className="md:col-span-3"><Label className="text-xs">Program</Label><Input value={draft.program} onChange={(e)=>setDraft({...draft, program: e.target.value})} placeholder="K-12 / IB / Cambridge…" data-testid="stu-new-program" className="mt-1" /></div>
          <div className="md:col-span-2"><Label className="text-xs">Course type</Label>
            <Select value={draft.course_type || "_none"} onValueChange={(v)=>setDraft({...draft, course_type: v === "_none" ? "" : v})}>
              <SelectTrigger data-testid="stu-new-course-type" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{COURSE_TYPES.map(c=><SelectItem key={c.value||"_none"} value={c.value||"_none"}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs">CEFR</Label>
            <Select value={draft.cefr_level || "_none"} onValueChange={(v)=>setDraft({...draft, cefr_level: v === "_none" ? "" : v})}>
              <SelectTrigger data-testid="stu-new-cefr" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">—</SelectItem>{LEVELS.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs">Tuition rate ¥ / hour</Label><Input type="number" min="0" step="10" value={draft.tuition_rate_rmb} onChange={(e)=>setDraft({...draft, tuition_rate_rmb: e.target.value})} data-testid="stu-new-rate" className="mt-1" /></div>
          <div className="md:col-span-3"><Label className="text-xs">Watch reason (optional)</Label>
            <Select value={draft.watch_reason || "_none"} onValueChange={(v)=>setDraft({...draft, watch_reason: v === "_none" ? "" : v, watch_flag: v !== "_none"})}>
              <SelectTrigger data-testid="stu-new-watch" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">—</SelectItem>{WATCH_REASONS.map(w=><SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3"><Label className="text-xs">Watch note</Label><Input value={draft.watch_note} onChange={(e)=>setDraft({...draft, watch_note: e.target.value})} data-testid="stu-new-watch-note" className="mt-1" /></div>
          <div className="md:col-span-12"><Button type="submit" disabled={busy} data-testid="stu-new-submit" className="rounded-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))]"><Check className="w-4 h-4 mr-1.5" />{busy ? "…" : "Add student"}</Button></div>
        </form>
      )}

      <div className="overflow-x-auto bg-white border border-[hsl(var(--border))] rounded-2xl">
        <table className="w-full text-sm" data-testid="stu-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
              <th className="p-3 font-normal">Name</th>
              <th className="p-3 font-normal">Contact</th>
              <th className="p-3 font-normal">Email</th>
              <th className="p-3 font-normal">CEFR</th>
              <th className="p-3 font-normal">Course</th>
              <th className="p-3 font-normal">Program</th>
              <th className="p-3 font-normal">¥/h</th>
              <th className="p-3 font-normal">Watch-list</th>
              <th className="p-3 font-normal text-right">·</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`stu-row-${r.id}`} className={`border-b border-[hsl(var(--border))]/60 last:border-0 ${r.watch_flag ? 'bg-red-50/30' : ''}`}>
                <td className="p-2"><Input value={r.name || ""} onChange={(e)=>setRows(prev => prev.map(x => x.id === r.id ? { ...x, name: e.target.value } : x))} onBlur={(e)=>patchRow(r.id, { name: e.target.value })} data-testid={`stu-${r.id}-name`} className="h-7 px-2 text-sm w-32 border-none focus-visible:ring-1" /></td>
                <td className="p-2"><Input value={r.contact || ""} onChange={(e)=>setRows(prev => prev.map(x => x.id === r.id ? { ...x, contact: e.target.value } : x))} onBlur={(e)=>patchRow(r.id, { contact: e.target.value })} data-testid={`stu-${r.id}-contact`} className="h-7 px-2 text-sm w-36 border-none focus-visible:ring-1" /></td>
                <td className="p-2"><Input value={r.email || ""} onChange={(e)=>setRows(prev => prev.map(x => x.id === r.id ? { ...x, email: e.target.value } : x))} onBlur={(e)=>patchRow(r.id, { email: e.target.value })} data-testid={`stu-${r.id}-email`} className="h-7 px-2 text-sm w-44 border-none focus-visible:ring-1" type="email" /></td>
                <td className="p-2"><CefrCell row={r} onChange={(p)=>patchRow(r.id, p)} /></td>
                <td className="p-2">
                  <Select value={r.course_type || "_none"} onValueChange={(v)=>patchRow(r.id, { course_type: v === "_none" ? "" : v })}>
                    <SelectTrigger data-testid={`stu-${r.id}-course`} className="h-7 px-2 text-xs w-32"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{COURSE_TYPES.map(c=><SelectItem key={c.value||"_none"} value={c.value||"_none"}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-2"><Input value={r.program || ""} onChange={(e)=>setRows(prev => prev.map(x => x.id === r.id ? { ...x, program: e.target.value } : x))} onBlur={(e)=>patchRow(r.id, { program: e.target.value })} data-testid={`stu-${r.id}-program`} className="h-7 px-2 text-sm w-32 border-none focus-visible:ring-1" /></td>
                <td className="p-2"><Input type="number" min="0" step="10" value={r.tuition_rate_rmb ?? ""} onChange={(e)=>setRows(prev => prev.map(x => x.id === r.id ? { ...x, tuition_rate_rmb: e.target.value } : x))} onBlur={(e)=>patchRow(r.id, { tuition_rate_rmb: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`stu-${r.id}-rate`} className="h-7 px-2 text-sm w-20 border-none focus-visible:ring-1" /></td>
                <td className="p-2"><WatchCell row={r} onChange={(p)=>patchRow(r.id, p)} /></td>
                <td className="p-2 text-right whitespace-nowrap"><Button size="sm" variant="ghost" onClick={()=>del(r.id, r.name)} data-testid={`stu-${r.id}-delete`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-10 text-center text-[hsl(var(--muted-foreground))]">{rows.length === 0 ? "No students yet — add your first one above." : "No matches for this filter."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 mt-0.5 text-[hsl(var(--primary))]" />
        <span>CEFR level is auto-filled from the student&apos;s most recent completed placement test (matched by email). Pick a level manually to override — your choice sticks. Use <strong>Import CSV</strong> with columns: <code className="text-[10px]">name, contact, email, cefr_level, course_type, program, tuition_rate_rmb, watch_flag, watch_reason, watch_note, notes</code>.</span>
      </p>
    </div>
  );
}
